import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  Users,
  CreditCard,
  Sun,
  Moon,
} from "lucide-react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase"; 


/* ===== NETWORK LOGOS ===== */
import mtnLogo from "../assets/networks/mtn.png";
import airteltigoLogo from "../assets/networks/airteltigo.png";
import telecelLogo from "../assets/networks/telecel.png";

/* ================= HELPERS ================= */
const formatGhs = (x) => `GHS ${Number(x || 0).toFixed(2)}`;

const NETWORKS = [
  { name: "MTN", logo: mtnLogo },
  { name: "AirtelTigo", logo: airteltigoLogo },
  { name: "Telecel", logo: telecelLogo },
];

const DESCRIPTIONS = ["Non-Expiring", "Expiring"];

/* 🔥 NEW — NETWORK COLLECTION HELPER (NO LOGIC REMOVED) */
const getPackageCollection = (network) => {
  switch (network) {
    case "MTN":
      return "packages_MTN";
    case "AirtelTigo":
      return "packages_AirtelTigo";
    case "Telecel":
      return "packages_Telecel";
    default:
      return "packages_MTN";
  }
};

/* ================= COMPONENT ================= */
export default function AdminDashboard() {
  const navigate = useNavigate();

  /* THEME */
  const [theme, setTheme] = useState(
    localStorage.getItem("admin-theme") || "dark"
  );
  const isDark = theme === "dark";

  /* GLOBAL SWITCH */
  const [packagesActive, setPackagesActive] = useState(true);
  const [switchLoading, setSwitchLoading] = useState(false);


  /* NETWORK FILTER */
  const [activeNetwork, setActiveNetwork] = useState("MTN");

  /* PACKAGES */
  const [packages, setPackages] = useState([]);

  /* FORM */
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    id: "",
    price: "",
    description: "Non-Expiring",
    network: "MTN",
    outOfStock: false,
  });

  /* ================= LOAD DATA ================= */
  useEffect(() => {
  async function load() {
    const ref = doc(db, "settings", "packages");
    const snap = await getDoc(ref);

    if (snap.exists()) {
      setPackagesActive(snap.data().packagesActive);
    } else {
      await setDoc(ref, { packagesActive: true });
      setPackagesActive(true);
    }
  }

  load();
}, []);

 

 

  useEffect(() => {
  async function load() {
    const colName = getPackageCollection(activeNetwork);
    const snap = await getDocs(collection(db, colName));
    setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));

    setEditingId(null);
    setForm({
      id: "",
      price: "",
      description: "Non-Expiring",
      network: activeNetwork,
      outOfStock: false,
    });
  }

  load();
}, [activeNetwork]);

  async function toggleGlobalPackages() {
    try {
      setSwitchLoading(true);
      await updateDoc(doc(db, "settings", "packages"), {
        packagesActive: !packagesActive,
      });
      setPackagesActive(!packagesActive);
    } finally {
      setSwitchLoading(false);
    }
  }

  /* ================= PACKAGES ================= */
  async function loadPackages() {
    const colName = getPackageCollection(activeNetwork);
    const snap = await getDocs(collection(db, colName));
    setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }

  const filteredPackages = packages
  .filter((p) => p.network === activeNetwork)
  .map((p) => {
    // extract number from ID e.g. MTN_10GB → 10
    const match = p.id.match(/\d+/);
    return {
      ...p,
      gb: match ? Number(match[0]) : 0,
    };
  })
  .sort((a, b) => a.gb - b.gb);


  function startEdit(pkg) {
    setEditingId(pkg.id);
    setForm({
      id: pkg.id,
      price: pkg.price,
      description: pkg.description || "Non-Expiring",
      network: pkg.network,
      outOfStock: pkg.outOfStock || false,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm({
      id: "",
      price: "",
      description: "Non-Expiring",
      network: activeNetwork,
      outOfStock: false,
    });
  }

 async function savePackage() {
  if (!form.id || !form.price) {
    alert("Package ID and price required");
    return;
  }

  const colName = getPackageCollection(form.network);

  const packageDocId = editingId
    ? editingId
    : `${form.network}_${form.id}`;

  await setDoc(doc(db, colName, packageDocId), {
    price: Number(form.price),
    description: form.description,
    network: form.network,
    outOfStock: form.outOfStock,
    updatedAt: serverTimestamp(),
  });

  resetForm();
  loadPackages();
}


  async function deletePackage(id) {
    if (!window.confirm("Delete this package?")) return;
    const colName = getPackageCollection(activeNetwork);
    await deleteDoc(doc(db, colName, id));
    loadPackages();
  }

  /* ================= LOGOUT ================= */
  const handleLogout = async () => {
      await signOut(auth);
      navigate("/agent/login");
    };

  /* ================= THEME CLASSES ================= */
  const pageBg = isDark ? "bg-slate-900 text-white" : "bg-gray-100 text-gray-900";
  const cardBg = isDark
    ? "bg-slate-800 border-slate-700"
    : "bg-white border-gray-200";
  const subBg = isDark ? "bg-slate-900" : "bg-gray-50";
  const inputBg = isDark ? "bg-slate-700" : "bg-gray-100";

  /* ================= UI ================= */
  return (
    <div className={`min-h-screen p-4 md:p-6 pb-24 ${pageBg}`}>
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h1 className="text-3xl font-extrabold text-sky-400">
            Admin Dashboard
          </h1>

          <div className="hidden md:flex gap-3 items-center">
            <NavBtn onClick={() => navigate("/admin/dashboard")}>DashBoard</NavBtn>
            <NavBtn onClick={() => navigate("/admin/users")}>👤 Users</NavBtn>
            <NavBtn onClick={() => navigate("/admin/transactions")}>💳 Transactions</NavBtn>
            <NavBtn onClick={() => navigate("/admin/agent-prices")}>
  💰 Agent Prices
</NavBtn>


            <button
              onClick={() => {
                const next = isDark ? "light" : "dark";
                setTheme(next);
                localStorage.setItem("admin-theme", next);
              }}
              className={`px-4 py-3 rounded-xl ${
                isDark ? "bg-slate-700" : "bg-gray-200"
              }`}
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            <NavBtn onClick={handleLogout} danger>
              <LogOut size={16} /> Logout
            </NavBtn>
          </div>
        </div>

        {/* GLOBAL SWITCH */}
        <div className={`p-5 rounded-xl border mb-8 ${cardBg}`}>
          <div className="flex justify-between items-center gap-4">
            <div>
              <h3 className="font-bold text-lg">
                Package Availability
              </h3>
              <p className="text-sm opacity-70">
                Controls all packages on User-end
              </p>
            </div>
            <button
              onClick={toggleGlobalPackages}
              disabled={switchLoading}
              className={`px-6 py-2 rounded-full font-bold ${
                packagesActive ? "bg-green-600" : "bg-red-600"
              }`}
            >
              {packagesActive ? "Active" : "offline"}
            </button>
          </div>
        </div>


        {/* NETWORK BUTTONS */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {NETWORKS.map((net) => (
            <button
              key={net.name}
              onClick={() => setActiveNetwork(net.name)}
              className={`p-4 rounded-2xl border transition flex flex-col items-center ${
                activeNetwork === net.name
                  ? "border-sky-500 bg-sky-500/20 scale-105"
                  : cardBg
              }`}
            >
              <img src={net.logo} alt={net.name} className="h-10 mb-2" />
              <span className="font-bold">{net.name}</span>
            </button>
          ))}
        </div>

        {/* FORM */}
        <div className={`p-6 rounded-xl border mb-8 ${cardBg}`}>
          <h2 className="text-xl font-bold mb-4">
            {editingId ? "Edit Package" : `Add ${activeNetwork} Package`}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <input
              placeholder="Package ID"
              value={form.id}
              disabled={!!editingId}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              className={`p-3 rounded ${inputBg}`}
            />
            <input
              type="number"
              placeholder="Price"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className={`p-3 rounded ${inputBg}`}
            />
            <select
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className={`p-3 rounded ${inputBg}`}
            >
              {DESCRIPTIONS.map((d) => (
                <option key={d}>{d}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.outOfStock}
                onChange={(e) =>
                  setForm({ ...form, outOfStock: e.target.checked })
                }
              />
              Out of Stock
            </label>
          </div>

          <div className="mt-6 flex gap-3">
            <button
              onClick={savePackage}
              className="px-6 py-3 rounded-xl font-bold bg-sky-600 hover:bg-sky-700 transition"
            >
              {editingId ? "Update Package" : "Add Package"}
            </button>

            {editingId && (
              <button
                onClick={resetForm}
                className="px-6 py-3 rounded-xl font-bold bg-gray-500 hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className={`overflow-x-auto rounded-xl border ${cardBg}`}>
          <table className="w-full text-sm">
            <thead className={subBg}>
              <tr>
                <th className="p-3">Package</th>
                <th className="p-3">Description</th>
                <th className="p-3">Price</th>
                <th className="p-3">Status</th>
                <th className="p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredPackages.map((pkg) => (
                <tr key={pkg.id} className="border-t text-center">
                  <td className="p-3 font-bold">{pkg.id}</td>
                  <td className="p-3">{pkg.description}</td>
                  <td className="p-3 text-sky-500">
                    {formatGhs(pkg.price)}
                  </td>
                  <td className="p-3 font-bold">
                    {pkg.outOfStock ? (
                      <span className="text-red-500">OUT</span>
                    ) : (
                      <span className="text-green-500">ACTIVE</span>
                    )}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => startEdit(pkg)}
                      className="px-3 py-1 bg-yellow-500 rounded font-bold mr-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deletePackage(pkg.id)}
                      className="px-3 py-1 bg-red-600 rounded font-bold"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOBILE NAV */}
      <div
        className={`fixed bottom-0 left-0 right-0 md:hidden border-t flex justify-around py-3 ${
          isDark
            ? "bg-black/90 border-white/10"
            : "bg-white border-gray-200"
        }`}
      >
        <MobileNav icon={<LayoutDashboard size={22} />} label="Dashboard" link="" />
<MobileNav icon={<Users size={22} />} label="Users" link="/admin/users" />
<MobileNav icon={<CreditCard size={22} />} label="Txns" link="/admin/transactions" />
<MobileNav
  icon={<CreditCard size={22} />}
  label="Agent Prices"
  link="/admin/agent-prices"
/>



        <button
          onClick={() => {
            const next = isDark ? "light" : "dark";
            setTheme(next);
            localStorage.setItem("admin-theme", next);
          }}
          className="flex flex-col items-center text-yellow-500"
        >
          {isDark ? <Sun size={22} /> : <Moon size={22} />}
          <span className="text-xs">Theme</span>
        </button>
        <button
          onClick={handleLogout}
          className="flex flex-col items-center text-red-500"
        >
          <LogOut size={22} />
          <span className="text-xs">Logout</span>
        </button>
      </div>
    </div>
  );
}

/* ================= UI HELPERS ================= */

const NavBtn = ({ children, onClick, danger }) => (
  <button
    onClick={() =>
      typeof onClick === "string"
        ? (window.location.href = onClick)
        : onClick()
    }
    className={`px-4 py-3 rounded-xl font-bold flex items-center gap-2 ${
      danger ? "bg-red-600" : "bg-sky-600"
    }`}
  >
    {children}
  </button>
);

const MobileNav = ({ icon, label, link }) => (
  <button
    onClick={() => link && (window.location.href = link)}
    className="flex flex-col items-center text-gray-400"
  >
    {icon}
    <span className="text-xs">{label}</span>
  </button>
);
